import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Check, X, Truck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReminderSignup from "@/components/ReminderSignup";

/**
 * PostcodeChecker — checks Perth metro deliverability (6000-6199)
 * If deliverable → shows success. If not → shows email waitlist capture.
 */
export default function PostcodeChecker({ variant = "hero" }) {
  const [postcode, setPostcode] = useState("");
  const [state, setState] = useState("idle"); // idle | deliverable | undeliverable | joined
  const [zone, setZone] = useState(null);     // { display_name, cadence_label, next_delivery_date_pretty, ... }
  const [email, setEmail] = useState("");
  const [suburb, setSuburb] = useState("");
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4}$/.test(postcode.trim())) {
      toast.error("Enter a valid 4-digit postcode");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads/postcode-check", { postcode: postcode.trim() });
      setZone(data.zone_info || null);
      setState(data.deliverable ? "deliverable" : "undeliverable");
      // Sept 2026 — stash a checked postcode into localStorage so Meta CAPI
      // events fired on subsequent pages (ViewContent on box/product detail,
      // AddToCart on the first add) carry `zp` in user_data. Big lift on
      // Event Match Quality for shoppers who use the delivery checker
      // BEFORE they add to cart — which is most of them.
      try {
        localStorage.setItem("tbb_delivery_postcode", postcode.trim());
      } catch { /* private mode — safe to ignore */ }
    } catch {
      toast.error("Couldn't check right now — please try again");
    } finally {
      setLoading(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await api.post("/leads/postcode-waitlist", {
        postcode: postcode.trim(),
        email: email.trim(),
        suburb: suburb.trim() || undefined,
      });
      // Waitlist submitters also give us match keys — stash them for CAPI.
      try {
        localStorage.setItem("tbb_capture_email", email.trim().toLowerCase());
        if (suburb.trim()) localStorage.setItem("tbb_capture_city", suburb.trim());
      } catch { /* private mode */ }
      setState("joined");
    } catch {
      toast.error("Couldn't add you to the waitlist — please try again");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setState("idle"); setPostcode(""); setEmail(""); setSuburb(""); };

  const inputCls =
    "w-full px-4 py-3 text-sm border border-[color:var(--tbb-line)] bg-white focus:outline-none focus:border-black";

  const isDark = variant === "hero";

  return (
    <div
      className={`w-full max-w-2xl ${isDark ? "bg-white/95 backdrop-blur text-[color:var(--tbb-ink)]" : "bg-[color:var(--tbb-bg-soft)]"} p-5 md:p-6 border-l-4 border-[color:var(--tbb-burgundy)]`}
      data-testid="postcode-checker"
    >
      <div className="flex items-center gap-2 mb-3">
        <Truck size={16} className="text-[color:var(--tbb-burgundy)]" />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
          Delivery check · Perth Metro +{" "}
          <Link
            to="/meat-delivery-bunbury"
            className="underline decoration-[color:var(--tbb-burgundy)] decoration-2 underline-offset-2 hover:text-[color:var(--tbb-burgundy)]"
            data-testid="postcode-bunbury-inline-link"
          >
            Bunbury
          </Link>
        </span>
      </div>

      {state === "idle" && (
        <form onSubmit={check} className="flex flex-col sm:flex-row gap-2" data-testid="postcode-form">
          <div className="relative flex-1">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--tbb-muted)]" />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              placeholder="Enter your postcode (e.g. 6000)"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className={`${inputCls} pl-9`}
              data-testid="postcode-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-burgundy shrink-0 justify-center"
            data-testid="postcode-check-btn"
          >
            {loading ? "Checking…" : "Check Delivery"}
          </button>
        </form>
      )}

      {state === "deliverable" && (
        <div data-testid="postcode-deliverable">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[color:var(--tbb-burgundy)] text-white flex items-center justify-center shrink-0">
              <Check size={16} strokeWidth={3} />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg leading-tight">
                We deliver to <span className="text-[color:var(--tbb-burgundy)]">{postcode}</span>.
              </div>
              {zone?.key === "bunbury" ? (
                <div className="text-xs text-[color:var(--tbb-muted)] mt-1" data-testid="postcode-zone-bunbury">
                  <b>{zone.display_name}</b> · {zone.cadence_label}. Next drop: <b>{zone.next_delivery_date_pretty}</b>.
                  <br/>Cut-off: {zone.cutoff_label} · free delivery on baskets over ${zone.free_delivery_min}.
                </div>
              ) : (
                <div className="text-xs text-[color:var(--tbb-muted)] mt-1">
                  Perth Metro · Order by Thursday 11pm — packed Friday, delivered Saturday. Free delivery on every order.
                </div>
              )}
            </div>
            <button onClick={reset} className="text-xs underline text-[color:var(--tbb-muted)]" data-testid="postcode-reset">
              Change
            </button>
          </div>

          {/* Recommended card — routes to the RIGHT next step based on zone.
              Bunbury shoppers see the Bunbury landing (Fri-morning cadence, not
              Perth-Saturday). Perth Metro shoppers see the Family Feast Box —
              the site's most popular cross-sell target. */}
          {zone?.key === "bunbury" ? (
            <div
              className="mt-5 border border-[color:var(--tbb-line)] bg-white p-4 flex items-start gap-4"
              data-testid="postcode-bunbury-cta"
            >
              <img
                src="/hiw/butcher-box.jpg"
                alt="Bunbury delivery"
                className="w-24 h-24 md:w-28 md:h-28 object-cover shrink-0"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[color:var(--tbb-burgundy)]">
                  DELIVERING TO {zone.display_name?.toUpperCase() || "BUNBURY"}
                </div>
                <div className="font-display text-xl leading-tight mt-1">
                  Fri Morning · South West Delivery
                </div>
                <div className="text-xs text-[color:var(--tbb-muted)] mt-1">
                  Order by <b>Thursday 1pm</b> for a Friday morning drop across Bunbury,
                  Australind, Eaton and Dalyellup. Same premium cuts, packed cold.
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Link
                    to="/meat-delivery-bunbury"
                    className="btn-burgundy inline-flex items-center gap-1.5"
                    data-testid="postcode-bunbury-cta-link"
                  >
                    See Bunbury delivery <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/boxes"
                    className="text-xs underline text-[color:var(--tbb-muted)] hover:text-black"
                    data-testid="postcode-all-boxes-link-bunbury"
                  >
                    or see all 7 boxes
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="mt-5 border border-[color:var(--tbb-line)] bg-white p-4 flex items-start gap-4"
              data-testid="postcode-family-feast-rec"
            >
              <img
                src="/boxes/family-feast-box.jpg"
                alt="Family Feast Box"
                className="w-24 h-24 md:w-28 md:h-28 object-cover shrink-0"
                loading="lazy"
                width="112"
                height="112"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[color:var(--tbb-burgundy)]">
                  MOST POPULAR — START HERE
                </div>
                <div className="font-display text-xl leading-tight mt-1">Family Feast Box</div>
                <div className="text-xs text-[color:var(--tbb-muted)] mt-1">13.7kg of premium beef, lamb & chicken. Feeds a family of 4 for ~2 weeks.</div>
                <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <div className="font-display text-lg">$269</div>
                  <div className="text-[11px] text-[color:var(--tbb-muted)]">≈ $4.90 per 250g serve</div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Link
                    to="/boxes/family-feast-box"
                    className="btn-burgundy inline-flex items-center gap-1.5"
                    data-testid="postcode-family-feast-cta"
                  >
                    Start with the Family Feast <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/boxes"
                    className="text-xs underline text-[color:var(--tbb-muted)] hover:text-black"
                    data-testid="postcode-all-boxes-link"
                  >
                    or see all 7 boxes
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Reminder signup — optional, non-blocking */}
          <div className="mt-5 pt-4 border-t border-[color:var(--tbb-line)]" data-testid="postcode-reminder-block">
            <ReminderSignup
              source="postcode_check"
              postcode={postcode}
              label="Get a reminder before Thursday's cutoff"
              buttonLabel="Remind me"
              microcopy="Optional — one email a week, unsubscribe anytime."
              testidPrefix="postcode-reminder"
            />
          </div>
        </div>
      )}

      {state === "undeliverable" && (
        <div data-testid="postcode-undeliverable">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[color:var(--tbb-ink)] text-white flex items-center justify-center shrink-0">
              <X size={16} strokeWidth={3} />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg leading-tight">Not in your area yet.</div>
              <div className="text-xs text-[color:var(--tbb-muted)] mt-1">
                We&apos;re currently delivering across Perth metro (6000–6199) and expanding to greater WA in 2026. Join the waitlist — we&apos;ll email you the moment we deliver to <strong>{postcode}</strong>.
              </div>
            </div>
          </div>
          <form onSubmit={joinWaitlist} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <input
              type="text"
              placeholder="Suburb (optional)"
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              className={inputCls}
              data-testid="postcode-waitlist-suburb"
            />
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <input
                type="email"
                required
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="postcode-waitlist-email"
              />
              <button disabled={loading} className="btn-burgundy justify-center" data-testid="postcode-waitlist-submit">
                {loading ? "Joining…" : "Join Waitlist"}
              </button>
            </div>
          </form>
          <button onClick={reset} className="text-[11px] underline text-[color:var(--tbb-muted)] mt-3" data-testid="postcode-try-another">
            Try another postcode
          </button>
        </div>
      )}

      {state === "joined" && (
        <div className="flex items-start gap-3" data-testid="postcode-joined">
          <div className="w-8 h-8 rounded-full bg-[color:var(--tbb-burgundy)] text-white flex items-center justify-center shrink-0">
            <Check size={16} strokeWidth={3} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg leading-tight">You&apos;re on the list.</div>
            <div className="text-xs text-[color:var(--tbb-muted)] mt-1">
              We&apos;ll email you at <strong>{email}</strong> the moment we deliver to <strong>{postcode}</strong>.
            </div>
          </div>
          <button onClick={reset} className="text-xs underline text-[color:var(--tbb-muted)]" data-testid="postcode-joined-reset">
            Check another
          </button>
        </div>
      )}
    </div>
  );
}
